import { resolveId } from '@medplum/core'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import { MealServingQueue } from '@seen/core/models/meal-queues/MealServingQueue'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { Badge, Button } from '@seen/ui'
import { IconToolsKitchen2 } from '@tabler/icons-react'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Card } from '#src/components/core/Card.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function ServingDetailPage(): JSX.Element {
  const navigate = useNavigate()
  const medplum = useMedplum()
  const [isProcessing, setIsProcessing] = useState(false)

  const { data: servingQueues, refetch: refetchServing } = useSearch(
    MealServingQueue,
    MealServingQueue.currentQueueQuery()
  )

  const servingQueue = servingQueues?.[0]
  const nextParticipantRef = servingQueue?.firstParticipant

  const { data: participant } = useRead(
    Participant,
    resolveId(nextParticipantRef)
  )

  const { data: allergies } = useSearch(ExtendedAllergy, {
    patient: nextParticipantRef?.reference,
    'clinical-status': 'active',
  })

  const dietQuestionnaireQuery = useMemo(() => {
    if (!nextParticipantRef?.reference) {
      return undefined
    }
    return ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.DietQuestionnaire,
      subject: { reference: nextParticipantRef.reference },
    })
  }, [nextParticipantRef])

  const { data: dietQuestionnaireResult } = useSearchOne(
    ExtendedQuestionnaireResponse,
    dietQuestionnaireQuery,
    {
      query: {
        enabled: !!nextParticipantRef,
      },
    }
  )

  const handleDoneServing = useCallback(async (): Promise<void> => {
    if (!servingQueue || !nextParticipantRef) {
      return
    }

    setIsProcessing(true)

    // Remove participant from serving queue (meal service complete)
    const removedParticipant = servingQueue.removeFirstParticipant()
    if (!removedParticipant) {
      return
    }

    await medplum.updateResource(servingQueue.toFhirResource())
    await refetchServing()

    setIsProcessing(false)
  }, [servingQueue, nextParticipantRef, medplum, refetchServing])

  if (!servingQueues) {
    return (
      <PageLayout title="Meal Serving" className="h-screen">
        <Loader />
      </PageLayout>
    )
  }

  if (!servingQueue || !nextParticipantRef) {
    return (
      <PageLayout title="Meal Serving" className="h-screen">
        <div className="flex h-full flex-col items-center justify-center">
          <IconToolsKitchen2 size={64} className="mb-4 text-gray-400" />
          <h2 className="mb-2 text-3xl font-semibold text-gray-600">
            No participants in serving queue
          </h2>
          <p className="mb-8 text-xl text-gray-500">
            All meals have been served!
          </p>
          <Button onClick={() => navigate('/Patient?tab=serving')} size="lg">
            Back to Meal Queue
          </Button>
        </div>
      </PageLayout>
    )
  }

  if (!participant) {
    return (
      <PageLayout title="Meal Serving" className="h-screen">
        <Loader />
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Meal Serving" className="h-screen">
      <div className="mx-auto flex h-full max-w-4xl flex-col p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <IconToolsKitchen2 size={48} className="text-green-600" />
            <div>
              <h1 className="text-4xl font-bold">Meal Serving</h1>
              <p className="text-xl text-gray-600">
                Next participant to serve ({servingQueue.length} remaining)
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/Patient?tab=serving')}
            size="lg"
          >
            Back to Queue
          </Button>
        </div>

        {/* Participant Info */}
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="mb-4 text-2xl font-semibold">
              Participant Information
            </h2>
            <ParticipantNameplate participant={nextParticipantRef} />
          </div>
        </Card>

        {/* Allergies - Critical Safety Information */}
        <Card className="mb-6 border-red-200 bg-red-50">
          <div className="p-6">
            <h2 className="mb-2 flex items-center gap-2 text-2xl text-red-700">
              ⚠️ Allergies & Safety
            </h2>
            <p className="mb-4 text-lg text-red-600">
              Verify meal matches dietary restrictions before serving
            </p>
            {allergies && allergies.length > 0 ? (
              <div className="grid gap-3">
                {allergies.map((allergy, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-red-200 bg-white p-4"
                  >
                    <div>
                      <span className="text-xl font-semibold text-red-800">
                        {allergy.name}
                      </span>
                      {allergy.reaction && (
                        <p className="mt-1 text-gray-600">
                          Reaction: {allergy.reaction}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant={
                        allergy.criticality === 'high'
                          ? 'destructive'
                          : 'secondary'
                      }
                      className="px-3 py-1 text-lg"
                    >
                      {allergy.criticality ?? 'Unknown severity'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-center">
                <span className="text-xl font-semibold text-green-700">
                  ✅ No Known Allergies
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Dietary Requirements */}
        <Card className="mb-8">
          <div className="p-6">
            <h2 className="mb-2 text-2xl font-semibold">
              Dietary Requirements
            </h2>
            <p className="mb-4 text-lg text-gray-600">
              Confirm plated meal matches these requirements
            </p>
            {dietQuestionnaireResult ? (
              <DietaryInfo dietResponse={dietQuestionnaireResult} />
            ) : (
              <div className="py-8 text-center text-gray-500">
                <p className="text-xl">No dietary information available</p>
              </div>
            )}
          </div>
        </Card>

        {/* Action Button */}
        <div className="mt-auto flex justify-center">
          <Button
            onClick={handleDoneServing}
            disabled={isProcessing}
            size="lg"
            className="bg-purple-600 px-12 py-6 text-2xl hover:bg-purple-700"
          >
            {isProcessing ? 'Processing...' : 'Done - Meal Served'}
          </Button>
        </div>
      </div>
    </PageLayout>
  )
}

interface DietaryInfoProps {
  dietResponse: ExtendedQuestionnaireResponse
}

function DietaryInfo({ dietResponse }: DietaryInfoProps): JSX.Element {
  const diet = dietResponse.getAnswerValue('diet')
  const fluidConsistency = dietResponse.getAnswerValue('fluid-consistency')
  const restrictions = dietResponse.getAnswerValue('fluid-restrictions')
  const supplements = dietResponse.getAnswerValue('supplements')
  const dislikes = dietResponse.getAnswerValue('dislikes')
  const notes = dietResponse.getAnswerValue('notes')

  // Convert values to strings and handle null/undefined
  const dietStr = diet ? String(diet) : ''
  const fluidConsistencyStr = fluidConsistency ? String(fluidConsistency) : ''
  const restrictionsStr = restrictions ? String(restrictions) : ''
  const supplementsStr = supplements ? String(supplements) : ''
  const dislikesStr = dislikes ? String(dislikes) : ''
  const notesStr = notes ? String(notes) : ''

  const hasAnyDietaryInfo =
    dietStr || fluidConsistencyStr || restrictionsStr || supplementsStr
  const hasPreferences = dislikesStr || notesStr

  return (
    <div className="space-y-6">
      {/* Main Dietary Needs */}
      {hasAnyDietaryInfo ? (
        <div className="grid gap-4">
          {dietStr && (
            <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4">
              <span className="text-lg font-semibold text-blue-800">
                Diet Type:
              </span>
              <span className="text-lg text-blue-700">{dietStr}</span>
            </div>
          )}
          {fluidConsistencyStr && (
            <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 p-4">
              <span className="text-lg font-semibold text-orange-800">
                Fluid Consistency:
              </span>
              <span className="text-lg text-orange-700">
                {fluidConsistencyStr}
              </span>
            </div>
          )}
          {restrictionsStr && restrictionsStr.toLowerCase() !== 'none' && (
            <div className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <span className="text-lg font-semibold text-yellow-800">
                Fluid Restrictions:
              </span>
              <span className="text-lg text-yellow-700">{restrictionsStr}</span>
            </div>
          )}
          {supplementsStr && supplementsStr.toLowerCase() !== 'none' && (
            <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
              <span className="text-lg font-semibold text-green-800">
                Supplements:
              </span>
              <span className="text-lg text-green-700">{supplementsStr}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="py-4 text-center">
          <span className="text-lg text-gray-600">
            No specific dietary requirements
          </span>
        </div>
      )}

      {/* Preferences and Notes */}
      {hasPreferences && (
        <div className="space-y-4">
          {dislikesStr && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <h4 className="mb-2 text-lg font-semibold text-purple-800">
                Food Dislikes:
              </h4>
              <p className="text-lg text-purple-700">{dislikesStr}</p>
            </div>
          )}
          {notesStr && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-2 text-lg font-semibold text-gray-800">
                Additional Notes:
              </h4>
              <p className="text-lg text-gray-700">{notesStr}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
